import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'underscore';
import {
  FluentProvider, Theme, makeStyles, mergeClasses, tokens, webLightTheme, shorthands, Button,
} from '@fluentui/react-components';
import { DismissCircle20Filled } from '@fluentui/react-icons';
import { I18nProvider, useI18nContext } from '@bingads-webui/copilot-i18n';
import { Header, type IHeaderProps } from '@bingads-webui-react/copilot-ui-base';

import { type IDirectLineError } from './botTypes';
import { useAdsCopilotLogger } from './hooks/use-ads-copilot-logger';

const useCopilotErrorStyles = makeStyles({
  error: {
    ...shorthands.padding(tokens.spacingVerticalMNudge, tokens.spacingVerticalM),
    ...shorthands.border('1px', 'solid', tokens.colorPaletteCranberryBackground2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    color: '#242424',
    backgroundColor: '#fdf3f4',
    display: 'flex',
  },
  copilotErrorContent: {
    ...shorthands.flex(1),
  },
  icon: {
    marginRight: tokens.spacingHorizontalSNudge,
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    marginTop: tokens.spacingVerticalMNudge,
    fontWeight: tokens.fontWeightBold,
  },
  errorTitle: {
    ...shorthands.margin(0),
    fontSize: tokens.fontSizeBase200,
  },
  errorText: {
    ...shorthands.margin(0),
    lineHeight: tokens.lineHeightBase200,
    fontSize: tokens.fontSizeBase200,
  },
});

interface ICopilotErrorProps {
  errorTitle?: string;
  errorText?: string;
  showTryAgainButton?: boolean;
  tryAgainButtonText?: string;
  onRefresh?: () => void;
  isTokenExpiredAutoReloadEnabled?: boolean;
  onAutoReloading?: () => void;
}

export const CopilotError: React.FC<ICopilotErrorProps> = React.memo((props) => {
  const styles = useCopilotErrorStyles();
  const {
    errorTitle, errorText, onRefresh, tryAgainButtonText, showTryAgainButton,
  } = props;

  return (
    <div className={mergeClasses(styles.error, 'copilot-error')}>
      <DismissCircle20Filled className={styles.icon} primaryFill="#b10e1c" />
      <div className={mergeClasses('copilot-error-content', styles.copilotErrorContent)}>
        <h4 className={styles.errorTitle}>{errorTitle}</h4>
        <p className={styles.errorText}>{errorText}</p>
        {showTryAgainButton ? (
          <div className={styles.buttonWrapper}>
            <Button size="small" className={mergeClasses('try-again-btn', styles.button)} onClick={onRefresh}>{tryAgainButtonText}</Button>
          </div>
        ) : null}
      </div>
    </div>
  );
});

CopilotError.defaultProps = {
  errorTitle: 'There is some error here',
  errorText: 'Description of the error and how to solve it.',
  showTryAgainButton: true,
  tryAgainButtonText: 'Try again',
  onRefresh: _.noop,
};

interface DirectLineErrorViewProps {
  error: IDirectLineError,
  onRefresh: ICopilotErrorProps['onRefresh'],
  isTokenExpiredAutoReloadEnabled?: ICopilotErrorProps['isTokenExpiredAutoReloadEnabled'],
  onAutoReloading?: ICopilotErrorProps['onAutoReloading'],
}

export const DirectLineErrorView: React.FC<DirectLineErrorViewProps> = React.memo(({
  error,
  onRefresh,
  isTokenExpiredAutoReloadEnabled,
  onAutoReloading,
}) => {
  const i18n = useI18nContext()!;
  const logger = useAdsCopilotLogger();
  const isTokenExpired = useMemo(() => error.message === 'directLineExpiredToken', []);
  const [isAutoReloading, setIsAutoReloading] = useState(false);

  const onTryAgainButtonClick = useCallback(() => {
    if (onRefresh) {
      logger?.trace('[Copilot][DirectLineErrorView] Try Again button clicked');
      onRefresh();
    }
  }, [logger, onRefresh]);

  useEffect(() => {
    if (!isAutoReloading && isTokenExpired && isTokenExpiredAutoReloadEnabled && _.isFunction(onRefresh) && _.isFunction(onAutoReloading)) {
      logger?.trace('[Copilot][DirectLineErrorView] Expired token detected, auto-reloading...');
      setIsAutoReloading(true);
      onRefresh();
      onAutoReloading();
    }
  }, [
    isTokenExpired,
    isTokenExpiredAutoReloadEnabled,
    logger,
    onRefresh,
    onAutoReloading,
  ]);

  useEffect(() => {
    if (!isAutoReloading) {
      logger?.trace(`[Copilot][DirectLineErrorView] Error detected: isTokenExpired=${isTokenExpired}, error.message=${error.message}`);
    }
  }, [error, isAutoReloading, isTokenExpired, logger]);

  if (isAutoReloading) {
    return null;
  }

  const errorTitle = isTokenExpired ? i18n.getString(_TL_('Your session has expired')) : i18n.getString(_TL_('There is some error here'));
  const errorText = isTokenExpired ? i18n.getString(_TL_('The conversation is expired. Start a new chat to continue.')) : i18n.getString(_TL_("We've encountered an error and can't continue. Please refresh to start a new chat."));

  return (
    <CopilotError
      onRefresh={onTryAgainButtonClick}
      errorTitle={errorTitle}
      errorText={errorText}
      tryAgainButtonText={i18n.getString(_TL_('Try again'))}
    />
  );
});

DirectLineErrorView.defaultProps = {
  isTokenExpiredAutoReloadEnabled: false,
  onAutoReloading: undefined,
};

export const useErrorViewStyles = makeStyles({
  provider: {
    height: '100%',
  },
  wrapper: {
    height: '100%',
  },
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke1,
  },
  errorWrapper: {
    ...shorthands.flex(1),
    ...shorthands.padding(0, tokens.spacingVerticalL),
  },
});

interface IGlobalErrorViewViewProps extends IHeaderProps, ICopilotErrorProps {
  theme?: Theme;
  locale?: string;
}

export const GlobalErrorView: React.FC<IGlobalErrorViewViewProps> = React.memo((props) => {
  const styles = useErrorViewStyles();
  const {
    theme,
    locale,
  } = props;
  return (
    <FluentProvider theme={theme} className={mergeClasses(styles.provider, 'ads-copilot-preparing-view')} applyStylesToPortals={false}>
      <I18nProvider locale={locale}>
        <div className={styles.root}>
          <Header {...props} />
          <div className={mergeClasses(styles.errorWrapper, 'error-wrapper')}>
            <CopilotError {...props} />
          </div>
        </div>
      </I18nProvider>
    </FluentProvider>
  );
});

GlobalErrorView.defaultProps = {
  theme: webLightTheme,
  errorTitle: 'There is some error here',
  errorText: 'Description of the error and how to solve it.',
  showTryAgainButton: true,
  tryAgainButtonText: 'Try again',
  onRefresh: _.noop,
  locale: 'en-us',
};
